<template>
  <div class="base-faqs">
    <h2 v-if="title" class="base-faqs__title h2" :class="{ 'base-faqs__title--centered': centerTitle }">
      {{ title }}
    </h2>

    <BaseShowMore v-slot="{ open }" :active="isCollapsible">
      <TappAccordionWrapper v-if="filteredFaqs.length > 0" class="faq-accordion divide-y">
        <TappAccordion
          v-for="(item, index) in filteredFaqs"
          v-show="showAll || open || index < COLLAPSED_COUNT"
          :id="`faq-item-${item.id}`"
          :key="item.id"
          :title="item.question"
          v-bind="index === filteredFaqs.length - 1 ? lastFaqVisibilityAttrs : {}"
          :class="{
            'base-faqs__item--collapsible': !showAll && index >= COLLAPSED_COUNT,
            'base-faqs__item--collapsed': !showAll && !open && index >= COLLAPSED_COUNT,
          }"
          :data-asset-id="item.id"
          animate
          :attrs-button="{
            'data-analytics-category': 'faq',
            'data-analytics-label': item.question,
          }"
          :attrs-button-closed="{
            'data-analytics-action': 'faq_open',
          }"
          :attrs-button-open="{
            'data-analytics-action': 'faq_close',
          }"
        >
          <BaseRichText :html="item.answer" />
        </TappAccordion>
      </TappAccordionWrapper>
    </BaseShowMore>

    <div v-if="phoneNumber" class="info">
      {{ $t('missingQuestion') }}

      <a
        class="retention-faqs__phone"
        :href="`tel:${phoneNumber}`"
        data-analytics-category="navigation"
        data-analytics-action="footer_link_click"
        :data-analytics-label="phoneNumber"
      >
        {{ phoneNumber }}
      </a>
    </div>
  </div>
</template>

<script lang="ts">
  /**
   * How many questions stay visible before the "Toon alles" toggle takes over.
   */
  export const COLLAPSED_COUNT = 5;
</script>

<script setup lang="ts">
  const props = withDefaults(
    defineProps<{
      faqs: {
        id: number | string;
        question: string;
        answer: string;
      }[];
      phoneNumber?: string;
      centerTitle?: boolean;
      title?: string;
      /**
       * Render the whole list at once instead of hiding everything past the fifth
       * question behind "Toon alles". Defaults to false so existing callers keep the
       * collapsed behaviour.
       */
      showAll?: boolean;
    }>(),
    {
      showAll: false,
    },
  );

  const filteredFaqs = computed(() => props.faqs);

  const lastFaqVisibilityAttrs = {
    class: '_dynamic_visibility_trigger',
    'data-analytics-category': 'content_visibility',
    'data-analytics-action': 'content_view',
    'data-analytics-label': 'laatste_faq_gezien',
  };

  // No toggle when the editor asked for the full list, or when there is nothing to hide.
  const isCollapsible = computed(() => !props.showAll && filteredFaqs.value.length > COLLAPSED_COUNT);

  const faqItemsStructuredData = computed(() => {
    const mainEntity = filteredFaqs.value.map((item) => {
      return {
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          // TODO: We should probably sanatize this from the html tags?
          text: item.answer,
        },
      };
    });
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity,
    };
  });

  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(faqItemsStructuredData.value),
      },
    ],
  });
</script>

<style lang="postcss" scoped>
  .base-faqs {
    user-select: none;
    display: grid;
    gap: var(--spacing-4);
    width: 100%;

    @media (--screen-md) {
      gap: var(--spacing-10);
    }

    @media (prefers-reduced-motion: no-preference) {
      @supports (interpolate-size: allow-keywords) and (transition-behavior: allow-discrete) {
        .base-faqs__item--collapsible {
          overflow: clip;
          overflow-clip-margin: var(--spacing-1);

          interpolate-size: allow-keywords;
          height: auto;

          opacity: 1;

          transition:
            height 200ms ease-out,
            opacity 200ms ease-out,
            border-top-width 200ms ease-out,
            display 200ms allow-discrete;

          @starting-style {
            height: 0;
            border-top-width: 0;
            opacity: 0;
          }

          &.base-faqs__item--collapsed {
            height: 0;
            border-top-width: 0;
            opacity: 0;
          }
        }
      }
    }

    &__title {
      &--centered {
        text-align: center;
      }
    }

    :deep(.tapp-accordion) {
      border-top: 1px solid var(--colors-gray-400);
      border-bottom: 0 solid transparent;
    }

    /**
     * TappAccordion titles default to --typography-large-body-lg-highlight (700 1.125rem);
     * FAQ questions read better at the body size in the brand's heading face.
     *
     * The h5 token rather than a body token, because that is where each brand puts its
     * display face: CapitoliumHead on the Volkskrant, PublicoHeadline on Trouw and de
     * Morgen. Note it is not serif everywhere — Libelle's heading face is Nexa and
     * AutoWeek's is RobotoCondensed, both sans — which is intended: this follows the
     * brand's own identity rather than forcing a serif onto brands that do not use one.
     *
     * Taken as the token and not the `.h5` class, which would step up to 1.125rem at the
     * extra-large breakpoint and undo the 16px sizing this was set to.
     *
     * Overridden here rather than in TappAccordion, whose rule sits in a non-scoped style
     * block shared with seven other consumers — the checkout summaries, the Kiosk product
     * intro and bladcadeau among them. Narrowing it to BaseFaqs keeps those untouched.
     */
    :deep(.tapp-accordion__title) {
      font: var(--typography-small-h5);
    }

    :deep(.tapp-accordion__inner) {
      user-select: text;
      max-width: 55rem;
      margin-bottom: var(--spacing-4);
    }

    :deep(.tapp-accordion__icon) {
      color: var(--colors-primary-500);
    }
  }
</style>
